import { api } from './config';
import type {
  DriveFile, DriveParsedContent, NotionDatabase, NotionPage,
  NotionPageContent, NotionContentUpdateResult, NotionImageUpload, NotionPropertyField,
  GmailMessage, GmailLabel, GmailComposeData, GmailStatus, GmailSendAs,
  WixConversationList, WixMessageList, WixContactList, WixContactDetail, WixStatus,
  WixNotificationSettings,
  ServiceConfig, SyncStatus, SyncLog, WebhookStatus,
  ToolsConvertResult,
  ParsedQuotation, QuotationConvertRequest,
  TranslationLanguage, TranslationGlossaryEntry, TranslationResult, GlossaryImportResult,
  TranslationProviderStatus,
} from '../types/connected-services.types';

export const connectedServicesApi = {
  // ===== Sync =====
  getSyncStatus: () => api.get<{ data: SyncStatus[] }>('/admin/services/sync/status'),
  getSyncHistory: (params?: { service?: string; page?: number; size?: number }) =>
    api.get('/admin/services/sync/history', { params }),
  triggerSync: (serviceType: string) => api.post(`/admin/services/sync/${serviceType}`),

  // ===== Google Drive =====
  getDriveFiles: (params?: { driveId?: string; folderId?: string; size?: number }) =>
    api.get<{ data: DriveFile[] }>('/admin/services/drive/files', { params }),
  getDriveFileDetail: (fileId: string) =>
    api.get<{ data: DriveFile }>(`/admin/services/drive/files/${fileId}`),
  getDriveTree: (driveId?: string) =>
    api.get<{ data: DriveFile[] }>('/admin/services/drive/tree', { params: { driveId } }),
  getDriveParsedContent: (fileId: string) =>
    api.get<{ data: DriveParsedContent }>(`/admin/services/drive/files/${fileId}/parsed`),
  getDriveFilePreviewUrl: (fileId: string) =>
    `${api.defaults.baseURL}/admin/services/drive/files/${fileId}/preview`,
  getDriveFileDownloadUrl: (fileId: string) =>
    `${api.defaults.baseURL}/admin/services/drive/files/${fileId}/download`,

  // ===== Notion =====
  getNotionDatabases: () =>
    api.get<{ data: NotionDatabase[] }>('/admin/services/notion/databases'),
  getNotionPages: (dbId: string, params?: { size?: number }) =>
    api.get<{ data: NotionPage[] }>(`/admin/services/notion/databases/${dbId}/pages`, { params }),
  getNotionPageDetail: (pageId: string) =>
    api.get<{ data: NotionPage }>(`/admin/services/notion/pages/${pageId}`),
  getNotionPropertySchema: (pageId: string) =>
    api.get<{ data: NotionPropertyField[] }>(`/admin/services/notion/pages/${pageId}/property-schema`),
  /** 바뀐 속성만 담아 보냄 — 전량 전송은 다른 창의 변경을 되돌림 */
  updateNotionPageProperties: (pageId: string, properties: Record<string, string>) =>
    api.patch<{ data: NotionPage }>(`/admin/services/notion/pages/${pageId}/properties`, { properties }),
  getNotionPageContent: (pageId: string) =>
    api.get<{ data: NotionPageContent }>(`/admin/services/notion/pages/${pageId}/content`),
  updateNotionPageContent: (pageId: string, data: { content_markdown: string; base_content_hash: string }) =>
    api.put<{ data: NotionContentUpdateResult }>(`/admin/services/notion/pages/${pageId}/content`, data),
  uploadNotionImage: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ data: NotionImageUpload }>('/admin/services/notion/images', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ===== Gmail =====
  getGmailAuthUrl: () =>
    api.get<{ data: { url: string } }>('/admin/services/gmail/auth-url'),
  exchangeGmailToken: (code: string) =>
    api.post('/admin/services/gmail/exchange-token', { code }),
  disconnectGmail: () =>
    api.delete('/admin/services/gmail/disconnect'),
  getGmailStatus: () =>
    api.get<{ data: GmailStatus }>('/admin/services/gmail/status'),
  getGmailSendAs: () =>
    api.get<{ data: GmailSendAs[] }>('/admin/services/gmail/send-as'),
  getGmailMessages: (params?: { label?: string; maxResults?: number; pageToken?: string }) =>
    api.get<{ data: GmailMessage[] }>('/admin/services/gmail/messages', { params }),
  getGmailMessageDetail: (messageId: string) =>
    api.get<{ data: GmailMessage }>(`/admin/services/gmail/messages/${messageId}`),
  sendGmailMessage: (data: GmailComposeData) =>
    api.post('/admin/services/gmail/messages/send', data),
  getGmailLabels: () =>
    api.get<{ data: GmailLabel[] }>('/admin/services/gmail/labels'),
  createGmailLabel: (name: string) =>
    api.post('/admin/services/gmail/labels', { name }),
  deleteGmailLabel: (labelId: string) =>
    api.delete(`/admin/services/gmail/labels/${labelId}`),
  modifyGmailMessageLabels: (messageId: string, data: { addLabelIds?: string[]; removeLabelIds?: string[] }) =>
    api.post(`/admin/services/gmail/messages/${messageId}/labels`, data),

  // ===== Wix =====
  getWixStatus: () =>
    api.get<{ data: WixStatus }>('/admin/services/wix/status'),
  getWixConversations: (params?: { scanFrom?: number; pageSize?: number }) =>
    api.get<{ data: WixConversationList }>('/admin/services/wix/conversations', { params, timeout: 60000 }),
  getWixMessages: (conversationId: string, params?: { limit?: number; sortOrder?: string; cursor?: string }) =>
    api.get<{ data: WixMessageList }>(`/admin/services/wix/conversations/${conversationId}/messages`, { params }),
  getWixConversationDetail: (conversationId: string, params?: { messageLimit?: number }) =>
    api.get<{ data: Record<string, unknown> }>(`/admin/services/wix/conversations/${conversationId}/detail`, { params, timeout: 60000 }),
  getWixContacts: (params?: { limit?: number; offset?: number }) =>
    api.get<{ data: WixContactList }>('/admin/services/wix/contacts', { params }),
  getWixContactDetail: (contactId: string) =>
    api.get<{ data: WixContactDetail }>(`/admin/services/wix/contacts/${contactId}`),
  getWixNotificationSettings: () =>
    api.get<{ data: WixNotificationSettings }>(
      '/admin/services/wix/notifications/settings'),
  updateWixNotificationSettings: (data: { inbox_enabled?: boolean; contact_enabled?: boolean }) =>
    api.put<{ data: WixNotificationSettings }>(
      '/admin/services/wix/notifications/settings', data),

  // ===== Settings =====
  getSettings: () =>
    api.get<{ data: ServiceConfig[] }>('/admin/services/settings'),
  getDriveConfigs: () =>
    api.get<{ data: ServiceConfig[] }>('/admin/services/settings/drives'),
  addDriveConfig: (data: { config_value: string; display_name?: string; is_shared_drive?: boolean }) =>
    api.post('/admin/services/settings/drives', data),
  deleteDriveConfig: (id: number) =>
    api.delete(`/admin/services/settings/drives/${id}`),
  getNotionDbConfigs: () =>
    api.get<{ data: ServiceConfig[] }>('/admin/services/settings/notion-databases'),
  addNotionDbConfig: (data: { config_value: string; display_name?: string }) =>
    api.post('/admin/services/settings/notion-databases', data),
  deleteNotionDbConfig: (id: number) =>
    api.delete(`/admin/services/settings/notion-databases/${id}`),

  // ===== Webhook =====
  getWebhookStatus: () =>
    api.get<{ data: WebhookStatus[] }>('/admin/services/webhook/status'),

  // ===== Translation =====
  translateDocument: (file: File, sourceLang: string, targetLang: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sourceLang) formData.append('source_lang', sourceLang);
    formData.append('target_lang', targetLang);
    return api.post<{ data: TranslationResult }>('/admin/services/translation/translate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  downloadTranslation: (downloadId: string) =>
    api.get(`/admin/services/translation/download/${downloadId}`, { responseType: 'blob' }),
  getTranslationLanguages: () =>
    api.get<{ data: TranslationLanguage[] }>('/admin/services/translation/languages'),
  getGlossary: () =>
    api.get<{ data: TranslationGlossaryEntry[] }>('/admin/services/translation/glossary'),
  addGlossary: (data: { source_text: string; target_text: string; category?: string }) =>
    api.post('/admin/services/translation/glossary', data),
  deleteGlossary: (id: number) =>
    api.delete(`/admin/services/translation/glossary/${id}`),
  exportGlossary: () =>
    api.get('/admin/services/translation/glossary/export', { responseType: 'blob' }),
  importGlossary: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ data: GlossaryImportResult }>(
      '/admin/services/translation/glossary/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTranslationProviderStatus: () =>
    api.get<{ data: TranslationProviderStatus }>('/admin/services/translation/provider-status'),
  toggleGoogleFallback: (enabled: boolean) =>
    api.put<{ data: TranslationProviderStatus }>(
      '/admin/services/translation/google-fallback', { enabled }),

  // ===== File Tools =====
  toolsMergePdf: (files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post<{ data: ToolsConvertResult }>('/admin/services/tools/merge-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  toolsPdfToImages: (file: File, dpi: number = 150) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ data: ToolsConvertResult }>('/admin/services/tools/pdf-to-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      params: { dpi },
    });
  },
  toolsImagesToPdf: (files: File[], pageSize: string = 'A4', orientation: string = 'PORTRAIT') => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post<{ data: ToolsConvertResult }>('/admin/services/tools/images-to-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      params: { pageSize, orientation },
    });
  },
  toolsInsertImagesToPdf: (pdf: File, images: File[], position: string = 'END', pageSize: string = 'A4', orientation: string = 'PORTRAIT') => {
    const formData = new FormData();
    formData.append('pdf', pdf);
    images.forEach(f => formData.append('images', f));
    return api.post<{ data: ToolsConvertResult }>('/admin/services/tools/insert-images-to-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      params: { position, pageSize, orientation },
    });
  },
  toolsDownload: (cacheId: string) =>
    api.get(`/admin/services/tools/download/${cacheId}`, { responseType: 'blob' }),

  // ===== Quotation Converter =====
  quotationParse: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ data: ParsedQuotation }>('/admin/services/quotation/parse-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  quotationConvert: (request: QuotationConvertRequest) =>
    api.post<{ data: ToolsConvertResult }>('/admin/services/quotation/convert', request, {
      timeout: 120000,
    }),
  quotationDownload: (cacheId: string) =>
    api.get(`/admin/services/quotation/download/${cacheId}`, { responseType: 'blob' }),
};
